import { Icon, ShadowStyles, Text } from '@genial/components';
import { BoxRadius, Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import {
  DrawerHeaderProps,
  DrawerNavigationHelpers,
} from '@react-navigation/drawer/lib/typescript/src/types';
import { Box, Pressable, Tooltip } from 'native-base';
import React, { memo, useContext, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';

import { useIsBetaMenuItem } from '../../../../hooks/useIsBetaMenuItem';
import { betaBgColor, sidebarFocus } from '../../constants';
import { AppMenuContext, AppMenuItem } from '../../context';

export interface ItemProps {
  item: AppMenuItem;
  navigation: DrawerNavigationHelpers | DrawerHeaderProps['navigation'];
  expanded?: boolean;
}

export const styles = StyleSheet.create({
  container: {
    padding: Utils.Token.ToInt(Spacing, 'inline.nano'),
    borderRadius: Utils.Token.ToInt(BoxRadius, 'sm'),
    flexDirection: 'row',
    alignItems: 'center',
    gap: Utils.Token.ToInt(Spacing, 'inline.nano'),
  },
  signOutContainer: {
    alignSelf: 'flex-start',
  },
  containerHover: {
    backgroundColor: Color['brand.tertiary.lightest'],
  },
  containerFocused: {
    backgroundColor: Color['brand.tertiary.dark'],
  },
  titleContainer: {
    marginLeft: Utils.Token.ToInt(Spacing, 'inline.nano'),
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 1,
  },
});

export const MenuItem: React.FC<ItemProps> = memo(({ item, expanded, navigation }) => {
  const [isHovering, setIsHovering] = useState(false);
  const { currentRoute } = useContext(AppMenuContext);
  const isBeta = useIsBetaMenuItem(item.routeName);
  const isSignOutRoute = item.routeName === 'SignOut';
  const isFocused =
    currentRoute?.name === item.routeName ||
    sidebarFocus[item.routeName]?.includes(currentRoute?.name ?? '');

  const handleHoverIn = () => {
    setIsHovering(true);
  };

  const handleHoverOut = () => {
    setIsHovering(false);
  };

  const handlePress = () => {
    const newRouteIsCurrentRoute = currentRoute?.name === item.routeName;
    const newRoute = { screen: item.routeName, params: item.routeParams };

    const navigationReplace = (navigation as unknown as any)
      .replace as DrawerNavigationHelpers['navigate'];

    if (item.routeName === 'InviteLinkPlaceholder') {
      navigation.navigate('InviteLink');
      return;
    }

    if (newRouteIsCurrentRoute && navigationReplace) {
      return navigationReplace('AppDrawer', newRoute);
    }

    navigation.navigate('AppDrawer', newRoute);
  };

  const iconColor: ColorTypes.Option = useMemo(() => {
    let currentColor: ColorTypes.Option = isSignOutRoute
      ? 'brand.tertiary.principal'
      : 'neutral.medium';

    if (isFocused) {
      currentColor = 'neutral.white';
    }

    if (isHovering) {
      currentColor = 'brand.tertiary.dark';
    }

    return currentColor;
  }, [isFocused, isHovering, isSignOutRoute]);

  const bgColor = useMemo(() => {
    if (isSignOutRoute) {
      return Color['brand.tertiary.lightest'];
    }

    return Color['neutral.white'];
  }, [isSignOutRoute]);

  return (
    <Tooltip
      isDisabled={expanded}
      label={item.label}
      placement="right"
      padding={Spacing['inline.xxxs']}
      backgroundColor={Color['neutral.white']}
      style={{ ...ShadowStyles.Level[1] }}
      marginLeft={Spacing['inline.xxs']}
      borderRadius={BoxRadius.sm}
      _text={{
        color: Color['neutral.dark'],
        fontFamily: Typography['fontfamily.base.regular'],
        fontSize: Typography['fontsize.lg'],
        fontWeight: Typography['fontweight.semibold'],
        paddingTop: '1px',
      }}>
      <Pressable
        backgroundColor={bgColor}
        style={[
          styles.container,
          isSignOutRoute && styles.signOutContainer,
          isFocused && styles.containerFocused,
          isHovering && styles.containerHover,
        ]}
        onPress={handlePress}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}>
        <Icon name={item.icon.name} size={item.icon.size} color={Color[iconColor]} />
        {expanded && <Text color={iconColor}>{item.label}</Text>}
        {expanded && isBeta && (
          <Box
            padding={Spacing['stack.quarck']}
            borderRadius={BoxRadius.xs}
            backgroundColor={betaBgColor}>
            <Text color="neutral.white">Beta</Text>
          </Box>
        )}
        {!expanded && isBeta && (
          <Box
            backgroundColor={betaBgColor}
            borderRadius="full"
            width="11px"
            height="11px"
            position="absolute"
            right={Spacing['inline.nano']}
            top={Spacing['inline.nano']}
          />
        )}
      </Pressable>
    </Tooltip>
  );
});

MenuItem.displayName = 'MenuItem';
